import type { GoalMetric } from "@runlens/shared";
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from "@heroicons/react/24/outline";

interface GoalMetricsProps {
  metrics: GoalMetric[];
}

function getTimeframeLabel(metric: GoalMetric): string | null {
  if (metric.dataSource === "fact") return null;

  const computation = metric.sourceConfig.computation;
  switch (computation) {
    case "weekly_volume_km":
      return "This week";
    case "avg_z2_pace":
    case "long_run_distance_km":
      return "Last 4 weeks";
    case "best_5k_time":
      return "Last 30 days";
    case "session_completion_rate":
      return "All sessions";
    default:
      return null;
  }
}

function formatMetricValue(value: number, unit: string): string {
  switch (unit) {
    case "time": {
      const mins = Math.floor(value / 60);
      const secs = Math.round(value % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    }
    case "pace": {
      const mins = Math.floor(value / 60);
      const secs = Math.round(value % 60);
      return `${mins}:${secs.toString().padStart(2, "0")} /km`;
    }
    case "km":
      return `${value.toFixed(1)} km`;
    case "%":
      return `${Math.round(value)}%`;
    case "kg":
      return `${value.toFixed(1)} kg`;
    case "bpm":
      return `${Math.round(value)} bpm`;
    default:
      return String(value);
  }
}

function getProgress(metric: GoalMetric): number | null {
  if (!metric.targetValue || !metric.currentValue) return null;

  const current = metric.currentValue.value;
  const target = metric.targetValue.value;

  if (metric.direction === "lower_is_better") {
    // e.g., pace: starting at 6:00, target 5:00. Progress = how far from start toward target.
    // We need a baseline. Use 120% of target as assumed start if current > target.
    const baseline = Math.max(current, target * 1.2);
    if (baseline === target) return 100;
    const progress = ((baseline - current) / (baseline - target)) * 100;
    return Math.max(0, Math.min(100, progress));
  }

  // higher_is_better: progress = current / target
  if (target === 0) return 0;
  return Math.max(0, Math.min(100, (current / target) * 100));
}

function getProgressColor(progress: number | null): string {
  if (progress === null) return "bg-gray-200";
  if (progress >= 80) return "bg-green-500";
  if (progress >= 50) return "bg-amber-500";
  return "bg-red-400";
}

function getProgressTextColor(progress: number | null): string {
  if (progress === null) return "text-gray-400";
  if (progress >= 80) return "text-green-600";
  if (progress >= 50) return "text-amber-600";
  return "text-red-500";
}

function LeadingMetricCard({ metric }: { metric: GoalMetric }) {
  const progress = getProgress(metric);
  const progressColor = getProgressColor(progress);
  const textColor = getProgressTextColor(progress);
  const timeframe = getTimeframeLabel(metric);

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-amber-700 uppercase tracking-wider">
          Goal
        </span>
        {metric.direction === "lower_is_better" ? (
          <ArrowTrendingDownIcon className="w-4 h-4 text-amber-500" />
        ) : (
          <ArrowTrendingUpIcon className="w-4 h-4 text-amber-500" />
        )}
      </div>
      <h4 className="text-sm font-semibold text-gray-900">
        {metric.name}
        {timeframe && (
          <span className="ml-1.5 text-xs font-normal text-amber-600">({timeframe})</span>
        )}
      </h4>

      <div className="mt-2 flex items-baseline gap-2">
        {metric.currentValue ? (
          <span className="text-xl font-bold text-gray-900">
            {formatMetricValue(metric.currentValue.value, metric.unit)}
          </span>
        ) : (
          <span className="text-xl font-bold text-gray-300">--</span>
        )}
        {metric.targetValue && (
          <span className="text-sm text-gray-500">
            / {formatMetricValue(metric.targetValue.value, metric.unit)}
          </span>
        )}
      </div>

      {progress !== null && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className={`font-medium ${textColor}`}>
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressColor}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function LaggingMetricCard({ metric }: { metric: GoalMetric }) {
  const progress = getProgress(metric);
  const textColor = getProgressTextColor(progress);
  const timeframe = getTimeframeLabel(metric);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <span className="text-xs text-gray-500 truncate block">{metric.name}</span>
          {timeframe && (
            <span className="text-[10px] text-gray-400">{timeframe}</span>
          )}
        </div>
        {metric.direction === "lower_is_better" ? (
          <ArrowTrendingDownIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        ) : (
          <ArrowTrendingUpIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        )}
      </div>

      <div className="mt-1 flex items-baseline gap-1.5">
        {metric.currentValue ? (
          <span className="text-sm font-semibold text-gray-900">
            {formatMetricValue(metric.currentValue.value, metric.unit)}
          </span>
        ) : (
          <span className="text-sm font-semibold text-gray-300">--</span>
        )}
        {metric.targetValue && (
          <span className="text-xs text-gray-400">
            / {formatMetricValue(metric.targetValue.value, metric.unit)}
          </span>
        )}
      </div>

      {progress !== null && (
        <div className="mt-1.5 flex items-center gap-2">
          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${getProgressColor(progress)}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className={`text-xs font-medium ${textColor}`}>
            {Math.round(progress)}%
          </span>
        </div>
      )}
    </div>
  );
}

export function GoalMetrics({ metrics }: GoalMetricsProps) {
  if (metrics.length === 0) return null;

  const leading = metrics.filter((m) => m.metricType === "leading");
  const lagging = metrics
    .filter((m) => m.metricType === "lagging")
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="space-y-3">
      {/* Leading metric(s) */}
      {leading.map((metric) => (
        <LeadingMetricCard key={metric.id} metric={metric} />
      ))}

      {/* Lagging metrics grid */}
      {lagging.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {lagging.map((metric) => (
            <LaggingMetricCard key={metric.id} metric={metric} />
          ))}
        </div>
      )}
    </div>
  );
}
